var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
export const bookFromDoc = (doc) => {
    const data = doc.data();
    return {
        id: doc.id,
        isbn: data.isbn,
        title: data.title,
        borrowedBy: data.borrowedBy,
        updatedAt: data.updatedAt.toDate(),
        createdAt: data.createdAt.toDate(),
    };
};
const bookFromEntry = (entry) => {
    const { id, data } = entry;
    return {
        id,
        isbn: data.isbn,
        title: data.title,
        borrowedBy: data.borrowedBy,
        updatedAt: data.updatedAt.toDate(),
        createdAt: data.createdAt.toDate(),
    };
};
export class BookRepository {
    constructor(db) {
        this.db = db;
        this.collection = this.db.collection('books');
        this.bookListsCollection = this.db.collection('bookLists');
        this.findAllCachedBooks = async () => {
            const querySnapshot = await this.bookListsCollection.get();
            return querySnapshot.docs.reduce((acc, doc) => {
                return [...acc, ...doc.data().entries.map(bookFromEntry)];
            }, []);
        };
        this.findAllBooks = async () => {
            const querySnapshot = await this.collection.get();
            return querySnapshot.docs.map(bookFromDoc);
        };
        this.findBooks = async ({ after, limit }) => {
            const orderField = 'createdAt';
            const orderedCollection = this.collection.limit(limit).orderBy(orderField);
            const offsetCollection = after ? orderedCollection.startAfter(after.id) : orderedCollection;
            return offsetCollection.get().then(qs => qs.docs.map(bookFromDoc));
        };
        this.findBookById = async (bookId) => {
            return this.mkBookRefById(bookId)
                .get()
                .then(bookFromDoc);
        };
        this.findBooksByIsbn = async (isbn) => {
            const querySnapshot = await this.collection.where('isbn', '==', isbn).get();
            return querySnapshot.docs.map(bookFromDoc);
        };
        this.findBorrowedBooksByUserid = async (userId) => {
            const querySnapshot = await this.collection.where('borrowedBy', '==', userId).get();
            return querySnapshot.docs.map(bookFromDoc);
        };
        this.borrowBookById = async (id, userId) => {
            const bookRef = await this.db.runTransaction(async (tx) => {
                const ref = this.mkBookRefById(id);
                const borrowableBook = await tx.get(ref).then(bookFromDoc);
                if (!!borrowableBook.borrowedBy) {
                    throw new Error(`id(${id})は貸し出せません`);
                }
                tx.update(ref, { borrowedBy: userId, updatedAt: new Date() });
                return ref;
            });
            return bookRef.get().then(bookFromDoc);
        };
        this.returnBookById = async (id, userId) => {
            const bookRef = await this.db.runTransaction(async (tx) => {
                const ref = this.mkBookRefById(id);
                const borrowedBook = await tx.get(ref).then(bookFromDoc);
                if (borrowedBook.borrowedBy !== userId) {
                    throw new Error(`id(${id})は貸し出していないので返せません`);
                }
                tx.update(ref, { borrowedBy: null, updatedAt: new Date() });
                return ref;
            });
            return bookRef.get().then(bookFromDoc);
        };
        this.borrowBookByIsbn = async (isbn, userId) => {
            const bookIds = await this.findBooksByIsbn(isbn).then(books => books.map(book => book.id));
            const bookRef = await this.db.runTransaction(async (tx) => {
                const bookPromises = bookIds
                    .map(this.mkBookRefById)
                    .map(_ => tx.get(_))
                    .map(doc => doc.then(bookFromDoc));
                const books = await Promise.all(bookPromises);
                const borrowableBook = books.find(book => {
                    // TODO: 一人が何冊借りることも許すか
                    return !!book.borrowedBy ? false : true;
                });
                if (!borrowableBook) {
                    throw new Error(`isbn(${isbn})は貸し出せません`);
                }
                const ref = this.mkBookRefById(borrowableBook.id);
                tx.update(ref, { borrowedBy: userId, updatedAt: new Date() });
                return ref;
            });
            return bookRef.get().then(bookFromDoc);
        };
        this.returnBookByIsbn = async (isbn, userId) => {
            const bookIds = await this.findBooksByIsbn(isbn).then(books => books.map(book => book.id));
            const bookRef = await this.db.runTransaction(async (tx) => {
                const bookPromises = bookIds
                    .map(this.mkBookRefById)
                    .map(_ => tx.get(_))
                    .map(doc => doc.then(bookFromDoc));
                const books = await Promise.all(bookPromises);
                const borrowedBook = books.find(book => {
                    return book.borrowedBy === userId;
                });
                if (!borrowedBook) {
                    throw new Error(`isbn(${isbn})は貸し出していないので返せません`);
                }
                const ref = this.mkBookRefById(borrowedBook.id);
                tx.update(ref, { borrowedBy: null, updatedAt: new Date() });
                return ref;
            });
            return bookRef.get().then(bookFromDoc);
        };
        this.registerBook = async (bookData) => {
            const book = Object.assign({ createdAt: new Date(), updatedAt: new Date(), borrowedBy: null }, bookData);
            const bookRef = await this.collection.add(book);
            return bookRef.get().then(bookFromDoc);
        };
        this.editBookById = async (book) => {
            const bookRef = await this.db.runTransaction(async (tx) => {
                const ref = this.mkBookRefById(book.id);
                tx.update(ref, { title: book.title, isbn: book.isbn, updatedAt: new Date() });
                return ref;
            });
            return bookRef.get().then(bookFromDoc);
        };
        this.bulkUpdate = async (books) => {
            const batch = this.db.batch();
            const before = await this.collection.get();
            books.forEach((_a) => {
                var { id } = _a, bookBody = __rest(_a, ["id"]);
                const targetDoc = before.docs.find(doc => doc.id === id);
                if (targetDoc !== undefined) {
                    const { isbn, title, borrowedBy, createdAt } = bookBody;
                    const updateData = Object.entries({
                        isbn,
                        title,
                        borrowedBy,
                        updatedAt: new Date(),
                        createdAt,
                    })
                        .filter(([_, v]) => v !== undefined)
                        .reduce((acc, [key, value]) => (Object.assign(Object.assign({}, acc), { [key]: value })), {});
                    batch.set(targetDoc.ref, updateData);
                }
            });
            await batch.commit();
        };
        this.mkBookRefById = (bookId) => this.collection.doc(bookId);
    }
}
//# sourceMappingURL=BookRepository.js.map